import { Print } from "@mui/icons-material";
import { Box, Button, Chip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import { readStoredUser } from '../../helpers/authSession';
import { formatCurrencyInCents, formatDate, presentPhone, resolvePublicMediaUrl } from '../../helpers/formatters';
import { applyEmail, applyName } from '../../helpers/captureMode';
import { quoteStatusLabel } from '../../helpers/quotes';

function PrintQuotePage() {
    const { id } = useParams();
    const [quote, setQuote] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const user = readStoredUser() || {};
    const storeName = user.store_name || user.name || 'Orçamento';
    const logoUrl = user.profile_picture ? resolvePublicMediaUrl(user.profile_picture) : '';

    useEffect(() => {
        if (!id) {
            return;
        }
        setIsLoading(true);
        setDidNotLoad(false);
        api.get(`quotes/${id}`)
            .then((response) => {
                setQuote(response.data.data || response.data);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [id]);

    if (isLoading) {
        return <Loading />;
    }
    if (didNotLoad || !quote) {
        return <DidNotLoad />;
    }

    const products = Array.isArray(quote.products) ? quote.products : [];
    const shippingOptions = Array.isArray(quote.shipping_options) ? quote.shipping_options : [];
    const paymentOptions = Array.isArray(quote.payment_options) ? quote.payment_options : [];
    const productsSubtotal = products.reduce(
        (sum, product) => sum + (Number(product.base_price) || 0) * (Number(product.quantity) || 0),
        0,
    );

    return (
        <div className="print-quote-page">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;600;700&display=swap');

                .print-quote-page {
                    background: #fff;
                    color: #111;
                    min-height: 100vh;
                    padding: 24px;
                    font-family: "Source Sans 3", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                    -webkit-font-smoothing: antialiased;
                }
                .print-quote-sheet {
                    max-width: 820px;
                    margin: 0 auto;
                    background: #fff;
                    display: flex;
                    flex-direction: column;
                    min-height: calc(100vh - 48px);
                }
                .print-quote-body {
                    flex: 1 1 auto;
                }
                .print-quote-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    border-bottom: 2px solid #222;
                    padding-bottom: 16px;
                    margin-bottom: 32px;
                }
                .print-quote-brand {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                .print-quote-logo {
                    width: 72px;
                    height: 72px;
                    object-fit: cover;
                    border-radius: 8px;
                }
                .print-quote-store {
                    font-size: 1.6rem;
                    font-weight: 700;
                    margin: 0;
                    letter-spacing: -0.01em;
                }
                .print-quote-title {
                    font-size: 1.2rem;
                    margin: 4px 0 0;
                    font-weight: 600;
                    color: #333;
                }
                .print-quote-section {
                    margin-bottom: 32px;
                }
                .print-quote-section h2 {
                    font-size: 0.85rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    margin: 0 0 12px;
                    border-bottom: 1px solid #ddd;
                    padding-bottom: 8px;
                }
                .print-quote-table {
                    width: 100%;
                    border-collapse: collapse;
                }
                .print-quote-table th,
                .print-quote-table td {
                    border-bottom: 1px solid #e5e5e5;
                    padding: 10px 6px;
                    text-align: left;
                    font-size: 0.95rem;
                }
                .print-quote-table th {
                    font-weight: 700;
                }
                .print-quote-table .num {
                    text-align: right;
                    white-space: nowrap;
                }
                .print-quote-totals {
                    margin-top: 14px;
                    text-align: right;
                    font-size: 1rem;
                }
                .print-quote-totals .total {
                    font-size: 1.25rem;
                    font-weight: 700;
                    margin-top: 6px;
                }
                .print-quote-meta p,
                .print-quote-section p {
                    margin: 6px 0;
                    line-height: 1.5;
                }
                .print-quote-footer {
                    margin-top: 40px;
                    padding-top: 16px;
                    border-top: 1px solid #e8e8e8;
                    text-align: center;
                    color: #888;
                    font-size: 0.8rem;
                    line-height: 1.5;
                }
                .print-quote-footer a {
                    color: inherit;
                    text-decoration: none;
                }
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .print-quote-page,
                    .print-quote-page * {
                        visibility: visible;
                    }
                    .print-quote-page {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        padding: 0;
                    }
                    .print-quote-sheet {
                        min-height: auto;
                    }
                    .no-print {
                        display: none !important;
                    }
                }
            `}</style>

            <div className="print-quote-sheet">
                <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<Print />}
                        onClick={() => window.print()}
                    >
                        Imprimir
                    </Button>
                </Box>

                <div className="print-quote-body">
                    <header className="print-quote-header">
                        <div className="print-quote-brand">
                            {logoUrl ? (
                                <img className="print-quote-logo" src={logoUrl} alt={storeName} />
                            ) : null}
                            <div>
                                <p className="print-quote-store">{storeName}</p>
                                <p className="print-quote-title">Orçamento</p>
                            </div>
                        </div>
                        <div className="print-quote-meta" style={{ textAlign: 'right' }}>
                            <Chip
                                className="no-print"
                                size="small"
                                label={quoteStatusLabel(quote.status)}
                                sx={{ mb: 1 }}
                            />
                            <p>Emitido em: {quote.created_at ? formatDate(quote.created_at) : '—'}</p>
                            <p>Validade: {quote.valid_until ? formatDate(quote.valid_until, false) : '—'}</p>
                        </div>
                    </header>

                    <section className="print-quote-section">
                        <h2>Cliente</h2>
                        <p><strong>Nome:</strong> {applyName(quote.customer_name) || '—'}</p>
                        {quote.customer_phone ? (
                            <p><strong>Telefone:</strong> {presentPhone(quote.customer_phone)}</p>
                        ) : null}
                        {quote.customer_email ? (
                            <p><strong>E-mail:</strong> {applyEmail(quote.customer_email)}</p>
                        ) : null}
                    </section>

                    <section className="print-quote-section">
                        <h2>Produtos</h2>
                        <table className="print-quote-table">
                            <thead>
                                <tr>
                                    <th>Produto</th>
                                    <th className="num">Qtd</th>
                                    <th className="num">Unitário</th>
                                    <th className="num">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((product, index) => {
                                    const lineTotal = (Number(product.base_price) || 0) * (Number(product.quantity) || 0);
                                    return (
                                        <tr key={`${product.name}-${index}`}>
                                            <td>{product.name}</td>
                                            <td className="num">{product.quantity}</td>
                                            <td className="num">{formatCurrencyInCents(product.base_price)}</td>
                                            <td className="num">{formatCurrencyInCents(lineTotal)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="print-quote-totals">
                            <div>Subtotal: {formatCurrencyInCents(productsSubtotal)}</div>
                            {(Number(quote.discount) || 0) > 0 && (
                                <>
                                    <div>Desconto: −{formatCurrencyInCents(quote.discount)}</div>
                                    {quote.discount_reason ? (
                                        <div>Motivo: {quote.discount_reason}</div>
                                    ) : null}
                                </>
                            )}
                            <div className="total">Total: {formatCurrencyInCents(quote.amount)}</div>
                        </div>
                    </section>

                    <section className="print-quote-section">
                        <h2>Produção</h2>
                        <p><strong>Prazo de produção:</strong> {quote.production_days || 0} dia(s)</p>
                    </section>

                    {shippingOptions.length > 0 && (
                        <section className="print-quote-section">
                            <h2>Opções de frete</h2>
                            <table className="print-quote-table">
                                <thead>
                                    <tr>
                                        <th>Opção</th>
                                        <th className="num">Prazo</th>
                                        <th className="num">Custo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shippingOptions.map((option, index) => (
                                        <tr key={`${option.name}-${index}`}>
                                            <td>{option.name}</td>
                                            <td className="num">{option.delivery_days || 0} dia(s)</td>
                                            <td className="num">{formatCurrencyInCents(option.cost)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    )}

                    {paymentOptions.length > 0 && (
                        <section className="print-quote-section">
                            <h2>Opções de pagamento</h2>
                            <table className="print-quote-table">
                                <thead>
                                    <tr>
                                        <th>Forma</th>
                                        <th className="num">Parcelas</th>
                                        <th className="num">Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentOptions.map((option, index) => (
                                        <tr key={`${option.name}-${index}`}>
                                            <td>{option.name}</td>
                                            <td className="num">{option.installments || 1}x</td>
                                            <td className="num">{formatCurrencyInCents(option.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    )}

                    {quote.observations ? (
                        <section className="print-quote-section">
                            <h2>Observações</h2>
                            <Typography component="p" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                                {quote.observations}
                            </Typography>
                        </section>
                    ) : null}
                </div>

                <footer className="print-quote-footer">
                    <div>Orçamento gerado com o Acompanha 3D</div>
                    <div>
                        <a href="https://www.acompanha3d.com.br" target="_blank" rel="noopener noreferrer">
                            www.acompanha3d.com.br
                        </a>
                    </div>
                </footer>
            </div>
        </div>
    );
}

export default PrintQuotePage;
